import React from 'react';

import { StyleVariant, Text } from '../TextComponent';
import { Box } from '../lib/components';

/** Where the single-digit tiebreaker box sits: its left edge `right` px right of the score's center, raised by `up`. */
export type TiebreakerScoreOffset = { right: number; up: number };

// Figma: an 8-wide box centered on the right edge of a 20pt score column, 4px up.
export const TIEBREAKER_OFFSET: TiebreakerScoreOffset = { right: 6, up: 4 };

export type TiebreakerSize = 'none' | 'single' | 'wide';

export const getTiebreakerSize = (...scores: (number | undefined)[]): TiebreakerSize => {
    const present = scores.filter(s => s !== undefined);
    if (present.length === 0) return 'none';
    return present.some(s => s > 9) ? 'wide' : 'single';
};

// overhang = how far the box pokes past the 20pt column's right edge; the last column reserves it as trailing space.
export const getTiebreakerLayout = (size: TiebreakerSize, offset: TiebreakerScoreOffset, columnGap = 0) =>
    ({
        none: { width: 0, marginLeft: 0, gapAfter: columnGap, overhang: 0 },
        single: { width: 8, marginLeft: offset.right, gapAfter: columnGap, overhang: offset.right - 2 },
        wide: { width: 16, marginLeft: 4, gapAfter: 8, overhang: 10 },
    }[size]);

type TiebreakerScoreProps = {
    score: number;
    size: TiebreakerSize;
    variant: StyleVariant;
    offset?: TiebreakerScoreOffset;
};

/** Superscript beside a period score, shared by the scoreboard grid and the tennis matchup card. */
export const TiebreakerScore = ({ score, size, variant, offset = TIEBREAKER_OFFSET }: TiebreakerScoreProps) => {
    const { width, marginLeft } = getTiebreakerLayout(size, offset);
    return (
        <Box position="absolute" width={width} left="50%" top={-offset.up} style={{ marginLeft }}>
            <Text variant={variant} color="neutral400" textAlign="center" numberOfLines={1}>
                {score}
            </Text>
        </Box>
    );
};
